/**
 * =============================================================================
 * Hook: useCreatePayoutForCommission
 * Versão: 1.0.0 (2026-04-29)
 *
 * Descrição:
 *   Cria (ou reaproveita) um affiliate_payouts pendente para uma comissão
 *   aprovada via RPC `create_payout_for_commission`. O frontend então
 *   redireciona o admin para a tela Pagamentos para concluir o pagamento.
 *
 * IMPORTANTE:
 *   Este hook substitui o antigo `markPaidMutation` (mark_commission_paid_manual),
 *   que foi descontinuado para evitar comissões "pagas" sem aparecer no extrato
 *   de affiliate_payouts.
 * =============================================================================
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const log = createLogger('useCreatePayoutForCommission');

export interface CreatePayoutForCommissionResult {
  /** ID do payout criado ou reaproveitado. */
  payout_id: string;
  /** Valor (igual ao reward_value da comissão). */
  amount: number;
  /** Afiliado dono da comissão. */
  affiliate_id: string;
  /** True quando reaproveitou um payout pendente já existente. */
  reused: boolean;
}

export function useCreatePayoutForCommission() {
  const qc = useQueryClient();
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  return useMutation({
    mutationFn: async (
      commissionId: string,
    ): Promise<CreatePayoutForCommissionResult> => {
      if (!tenantId) {
        throw new Error('Tenant ativo não definido');
      }

      // RPC criada na migration v1.0.0 (29/04/2026).
      // Cast `as never` evita travar enquanto types.ts ainda não foi regenerado.
      const { data, error } = await supabase.rpc(
        'create_payout_for_commission' as never,
        {
          p_commission_id: commissionId,
          p_tenant_hint: tenantId,
        } as never,
      );

      if (error) {
        log.error('Falha ao criar payout para comissão', error);
        throw error;
      }

      return data as unknown as CreatePayoutForCommissionResult;
    },
    onSuccess: () => {
      // Invalida caches que mostram comissões e payouts.
      qc.invalidateQueries({ queryKey: ['referral-commissions-paginated'] });
      qc.invalidateQueries({ queryKey: ['referral-commissions-admin'] });
      qc.invalidateQueries({ queryKey: ['referral-payouts-paginated'] });
      qc.invalidateQueries({ queryKey: ['referral-dashboard-kpis'] });
    },
  });
}
